import api, { UPLOAD_REQUEST_TIMEOUT_MS } from "./api";
import {
  UnderwritingDecision,
  UnderwriterAssessmentFormData,
  UnderwritingAssessment,
  RefinanceOffer,
  RefinanceOfferPayload,
} from "@/types/underwriting";
import { PaginatedResponse } from "@/types/borrower";

export const underwritingService = {
  list: async (
    params: { page?: number; status?: string } = {}
  ): Promise<PaginatedResponse<UnderwritingDecision>> => {
    const response = await api.get<PaginatedResponse<UnderwritingDecision>>("/decisions/", {
      params,
    });
    return response.data;
  },

  getById: async (id: number): Promise<UnderwritingDecision> => {
    const response = await api.get<UnderwritingDecision>(`/decisions/${id}/`);
    return response.data;
  },

  getByBorrowerId: async (borrowerId: number): Promise<UnderwritingDecision[]> => {
    const response = await api.get<UnderwritingDecision[]>("/decisions/", {
      params: { borrower_id: borrowerId },
    });
    return response.data;
  },

  reprocess: async (id: number): Promise<UnderwritingDecision> => {
    const response = await api.post<UnderwritingDecision>(`/decisions/${id}/reprocess/`);
    return response.data;
  },

  createAssessment: async (
    data: UnderwriterAssessmentFormData
  ): Promise<UnderwritingAssessment> => {
    const response = await api.post<UnderwritingAssessment>("/assessments/", data);
    return response.data;
  },

  listAssessments: async (
    params: { page?: number; search?: string } = {}
  ): Promise<PaginatedResponse<UnderwritingAssessment>> => {
    const response = await api.get<PaginatedResponse<UnderwritingAssessment>>("/assessments/", {
      params,
    });
    return response.data;
  },

  getAssessmentById: async (id: number): Promise<UnderwritingAssessment> => {
    const response = await api.get<UnderwritingAssessment>(`/assessments/${id}/`);
    return response.data;
  },

  getAssessmentByBorrowerId: async (borrowerId: number): Promise<UnderwritingAssessment | null> => {
    try {
      const response = await api.get<UnderwritingAssessment>(
        `/assessments/by-borrower/${borrowerId}/`
      );
      return response.data;
    } catch (error: any) {
      // Return null if 204 No Content (no assessment found)
      if (error.response?.status === 204) {
        return null;
      }
      throw error;
    }
  },

  updateAssessment: async (
    id: number,
    data: UnderwriterAssessmentFormData
  ): Promise<UnderwritingAssessment> => {
    const response = await api.put<UnderwritingAssessment>(`/assessments/${id}/`, data);
    return response.data;
  },

  uploadFiles: async (
    assessmentId: number,
    files: File[]
  ): Promise<{ message: string; file_ids: number[] }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const response = await api.post<{ message: string; file_ids: number[] }>(
      `/assessments/${assessmentId}/upload-files/`,
      formData,
      {
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  listFiles: async (assessmentId: number): Promise<any[]> => {
    const response = await api.get<any[]>(`/assessments/${assessmentId}/files/`);
    return response.data;
  },

  downloadFile: async (fileId: number): Promise<string> => {
    // This would need a download endpoint - for now return the file URL
    // You may need to add a download endpoint similar to statements
    return `/api/v1/assessments/files/${fileId}/download/`;
  },

  deleteFile: async (assessmentId: number, fileId: number): Promise<void> => {
    await api.delete(`/assessments/${assessmentId}/files/${fileId}/`);
  },

  // ── Refinance tab: one mutable RefinanceOffer row per borrower ──
  getRefinanceOffer: async (borrowerId: number): Promise<RefinanceOffer | null> => {
    const response = await api.get<RefinanceOffer | null>(
      `/assessments/refinance-offer/${borrowerId}/`
    );
    return response.data ?? null;
  },

  saveRefinanceOffer: async (
    borrowerId: number,
    data: RefinanceOfferPayload
  ): Promise<RefinanceOffer> => {
    const response = await api.put<RefinanceOffer>(
      `/assessments/refinance-offer/${borrowerId}/`,
      data
    );
    return response.data;
  },
};
